/**
 * @fileoverview Provides the `useToast` hook for managing toast notifications. The hook allows adding, removing, and displaying toasts using the `ToastItem` interface and functions like `addToast`, `removeToast`, and `showMessage`. It manages a list of toast items in the `toasts` state.
 */

import { useState, useCallback } from 'react'

export interface ToastItem {
  id: string
  message: string
  duration?: number
  targetElement?: HTMLElement | null
}

export const useToast = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const addToast = useCallback(
    (message: string, duration?: number, targetElement?: HTMLElement | null) => {
      const id = Date.now().toString() + Math.random().toString(36).substr(2, 9)
      const newToast: ToastItem = {
        id,
        message,
        duration,
        targetElement,
      }

      setToasts((prev) => [...prev, newToast])
      return id
    },
    [],
  )

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const showMessage = useCallback(
    (message: string, duration?: number, targetElement?: HTMLElement | null) => {
      return addToast(message, duration, targetElement)
    },
    [addToast],
  )

  return {
    toasts,
    addToast,
    removeToast,
    showMessage,
  }
}
